import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  LinearProgress,
  Theme,
  useTheme,
} from "@mui/material";
import { useIntl } from "react-intl";
import { DropzoneAreaBase, FileObject } from "mui-file-dropzone";
import { FileSizes, getFileExtension } from "utils/mediaUtils";
import { useMemo, useState } from "react";
import SnackbarMessage from "components/SnackbarMessage";
import { SnackbarMessageProps } from "types/common-types";
import useGlobalContext from "hooks/globalData";
import { withTypes } from "react-final-form";
import translateError from "utils/translateError";
import { FORM_ERROR } from "final-form";
import { format } from "date-fns";
import MediaLibraryItemPreview from "pages/MediaLibraryPage/MediaLibraryItemPreview";
import { ProcessingModel } from "services/KnowfullyAdminClient";

const useStyles = (theme: Theme) => ({
  feedback: {
    width: "100%",
    mt: "20px",
  },
});

export type SubmissionStatusFileFormValues = {
  processingId?: string;
  fileContent?: File;
};

const { Form } = withTypes<SubmissionStatusFileFormValues>();

export type UploadSubmissionStatusDialogProps = {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onUpload: () => void;
  readonly processing?: ProcessingModel;
};

export function UploadSubmissionStatusDialog({
  open,
  onClose,
  onUpload,
  processing,
}: Readonly<UploadSubmissionStatusDialogProps>) {
  const { formatMessage: t } = useIntl();
  const [fileObject, setFileObject] = useState<FileObject | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);
  const { submissionService } = useGlobalContext();
  const styles = useStyles(useTheme());
  const fileExtension = getFileExtension(processing?.uploadingSubmissionStatusFileType);

  const init = useMemo<SubmissionStatusFileFormValues>(
    () => ({
      processingId: processing?.id,
      fileContent: fileObject?.file,
    }),
    [fileObject, processing]
  );

  const onSubmit = async (values: SubmissionStatusFileFormValues) => {
    try {
      setIsUploading(true);
      await submissionService.uploadSubmissionStatusFile(values.processingId, {
        data: values.fileContent,
        fileName: `${values.processingId}-${format(
          new Date(),
          "yyyy-MM-dd-HH-mm-ss-SSS"
        )}${fileExtension}`,
      });
      onUpload();
      onClose();
      setFileObject(null);
    } catch (err: any) {
      return { [FORM_ERROR]: translateError(err, t) };
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <>
      <Dialog fullWidth maxWidth={"md"} open={open}>
        {isUploading && <LinearProgress />}
        <DialogTitle>
          {t({
            id: "uploadSubmissionStatusFile",
          })}
        </DialogTitle>

        <Form
          onSubmit={onSubmit}
          initialValues={init}
          render={({ handleSubmit, submitting, submitError }) => {
            return (
              <form onSubmit={handleSubmit} noValidate>
                <DialogContent>
                  <Grid container rowSpacing={2}>
                    {fileObject && (
                      <Grid container justifyContent="center">
                        <MediaLibraryItemPreview
                          fileName={fileObject.file.name}
                          fileSize={fileObject.file.size}
                          contentType={fileObject.file.type}
                          onClose={() => setFileObject(null)}
                        />
                      </Grid>
                    )}
                    {!fileObject && (
                      <DropzoneAreaBase
                        acceptedFiles={[fileExtension]}
                        maxFileSize={FileSizes.Mb10}
                        filesLimit={1}
                        onAdd={(files: FileObject[]) => {
                          setFileObject(files[0]);
                        }}
                        fileObjects={[]}
                        showAlerts={["error", "info"]}
                      />
                    )}
                    {submitError && (
                      <Alert sx={styles.feedback} severity="error">
                        {submitError}
                      </Alert>
                    )}
                  </Grid>
                </DialogContent>

                <DialogActions>
                  <Button
                    type="submit"
                    color="primary"
                    disabled={submitting || !fileObject}
                  >
                    {t({ id: "confirmButton" })}
                  </Button>

                  <Button
                    disabled={submitting}
                    onClick={() => {
                      setFileObject(null);
                      onClose();
                    }}
                  >
                    {t({ id: "cancelButton" })}
                  </Button>
                </DialogActions>
              </form>
            );
          }}
        />
      </Dialog>
      <SnackbarMessage
        snackbarMessage={snackbarMessage}
        setSnackbarMessage={setSnackbarMessage}
      />
    </>
  );
}
